// Meeting assistant: transcript in (recording, audio file, or a Zoom /
// Teams .vtt / .srt / .txt export) → structured notes out (summary,
// decisions, action items, open questions, follow-up email).
//
// Every model call goes through the caller-supplied ask() → /api/chat, so
// plan gating, metering, data routing and Privacy Shield apply as usual.
// Long meetings are summarized in chunks, then merged.

export const MEETING_COMMAND = /^\/meeting\b\s*/i;
export const STT_MAX_BYTES = 24 * 1024 * 1024; // /api/stt accepts 25 MB
export const SEGMENT_MS = 4 * 60 * 1000;       // recorder restarts every 4 min
const CHUNK_CHARS = 40_000;                     // ≈ 10k tokens per summary call

export function fmtTime(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h ? `${h}:${pad(m)}:${pad(r)}` : `${pad(m)}:${pad(r)}`;
}

function cueSeconds(stamp) {
  const m = String(stamp).trim().replace(",", ".").match(/^(?:(\d+):)?(\d{1,2}):(\d{2})(?:\.\d+)?$/);
  if (!m) return null;
  return (Number(m[1] || 0) * 3600) + Number(m[2]) * 60 + Number(m[3]);
}

// WebVTT / SRT → "[mm:ss] Speaker: text" lines, merging consecutive cues
// from the same speaker. Plain text passes through untouched.
export function parseTranscriptText(raw, filename = "") {
  const text = String(raw || "").replace(/^﻿/, "").replace(/\r\n?/g, "\n");
  const isCaption = /\.(vtt|srt)$/i.test(filename) || /^WEBVTT/.test(text) || /^\d+\n\d{2}:\d{2}:\d{2}[,.]\d{3} --> /m.test(text);
  if (!isCaption) return text.trim();
  const out = [];
  for (const block of text.split(/\n{2,}/)) {
    const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
    const ti = lines.findIndex((l) => l.includes("-->"));
    if (ti < 0) continue;
    const start = cueSeconds(lines[ti].split("-->")[0]);
    let body = lines.slice(ti + 1).join(" ");
    if (!body) continue;
    let speaker = null;
    const v = body.match(/^<v(?:\.[^ >]*)?\s+([^>]+)>/);
    if (v) speaker = v[1].trim();
    body = body.replace(/<[^>]+>/g, "").trim();
    if (!speaker) { const s = body.match(/^([A-Z][\w .'-]{0,40}):\s+(.+)$/); if (s) { speaker = s[1].trim(); body = s[2]; } }
    const prev = out[out.length - 1];
    if (prev && prev.speaker === speaker && speaker) prev.text += " " + body;
    else out.push({ t: start, speaker, text: body });
  }
  return out.map((c) => `[${fmtTime(c.t)}] ${c.speaker ? c.speaker + ": " : ""}${c.text}`).join("\n");
}

// Split on line boundaries so no cue is cut in half.
export function chunkTranscript(text, max = CHUNK_CHARS) {
  const lines = String(text || "").split("\n");
  const chunks = [];
  let cur = "";
  for (const line of lines) {
    if (line.length > max) {
      if (cur) { chunks.push(cur); cur = ""; }
      for (let i = 0; i < line.length; i += max) chunks.push(line.slice(i, i + max));
      continue;
    }
    if (cur && cur.length + line.length + 1 > max) { chunks.push(cur); cur = ""; }
    cur = cur ? cur + "\n" + line : line;
  }
  if (cur.trim()) chunks.push(cur);
  return chunks;
}

const NOTES_SHAPE = `{
  "title": "short meeting title",
  "summary": "3-6 sentence summary",
  "decisions": ["decision made"],
  "action_items": [{ "task": "what", "owner": "who or null", "due": "when or null" }],
  "open_questions": ["unresolved question"],
  "follow_up_email": { "subject": "subject line", "body": "plain-text email to attendees recapping decisions and action items" }
}`;

export function notesPrompt(transcript, { context = "", part = null } = {}) {
  return [
    part ? `This is part ${part.index} of ${part.total} of a meeting transcript. Extract notes for THIS part only.` : "Here is a meeting transcript.",
    context ? `Context from the user: ${context}` : "",
    "Write meeting notes as JSON only (no prose, no code fence) in exactly this shape:",
    NOTES_SHAPE,
    "Rules: use only what was said; never invent owners or dates (use null); keep action items concrete and start them with a verb; write in the meeting's language.",
    "# Transcript",
    transcript,
  ].filter(Boolean).join("\n\n");
}

export function mergePrompt(partials, { context = "" } = {}) {
  return [
    "These are notes extracted from consecutive parts of ONE meeting. Merge them into a single set of notes: deduplicate, keep every distinct decision and action item, and write one coherent summary and one follow-up email.",
    context ? `Context from the user: ${context}` : "",
    "Answer as JSON only, in exactly this shape:",
    NOTES_SHAPE,
    "# Partial notes",
    JSON.stringify(partials),
  ].filter(Boolean).join("\n\n");
}

// Tolerant JSON extraction (code fences, leading prose).
export function parseNotes(text) {
  const s = String(text || "").replace(/```(?:json)?/gi, "");
  const a = s.indexOf("{"), b = s.lastIndexOf("}");
  if (a < 0 || b <= a) throw new Error("The model didn't return meeting notes — try again.");
  let j;
  try { j = JSON.parse(s.slice(a, b + 1)); } catch { throw new Error("The model's notes weren't valid JSON — try again."); }
  const arr = (x) => (Array.isArray(x) ? x : []);
  const str = (x) => (typeof x === "string" ? x.trim() : "");
  const nul = (x) => { const v = str(x); return v && !/^(null|none|n\/a|unknown|tbd)$/i.test(v) ? v : null; };
  return {
    title: str(j.title) || "Meeting notes",
    summary: str(j.summary),
    decisions: arr(j.decisions).map(str).filter(Boolean),
    action_items: arr(j.action_items).map((x) => (typeof x === "string" ? { task: x, owner: null, due: null } : { task: str(x && x.task), owner: nul(x && x.owner), due: nul(x && x.due) })).filter((x) => x.task),
    open_questions: arr(j.open_questions).map(str).filter(Boolean),
    follow_up_email: { subject: str(j.follow_up_email && j.follow_up_email.subject) || "Meeting follow-up", body: str(j.follow_up_email && j.follow_up_email.body) },
  };
}

export function notesToMarkdown(n, { date = null } = {}) {
  const lines = [`# ${n.title}`];
  if (date) lines.push("", `*${date}*`);
  if (n.summary) lines.push("", "## Summary", "", n.summary);
  if (n.decisions.length) lines.push("", "## Decisions", "", ...n.decisions.map((d) => `- ${d}`));
  if (n.action_items.length) {
    lines.push("", "## Action items", "", "| Task | Owner | Due |", "|---|---|---|");
    for (const a of n.action_items) lines.push(`| ${a.task.replace(/\|/g, "/")} | ${(a.owner || "—").replace(/\|/g, "/")} | ${(a.due || "—").replace(/\|/g, "/")} |`);
  }
  if (n.open_questions.length) lines.push("", "## Open questions", "", ...n.open_questions.map((q) => `- ${q}`));
  if (n.follow_up_email.body) lines.push("", "## Follow-up email", "", `**Subject:** ${n.follow_up_email.subject}`, "", n.follow_up_email.body);
  return lines.join("\n");
}

class Stopped extends Error { constructor() { super("stopped"); this.stopped = true; } }

// ask(prompt, { maxTokens }) → string
export async function summarizeMeeting({ transcript, context = "", ask, onProgress = () => {}, isStopped = () => false }) {
  const text = String(transcript || "").trim();
  if (!text) throw new Error("The transcript is empty.");
  const chunks = chunkTranscript(text);
  if (chunks.length === 1) {
    onProgress({ phase: "summarizing", done: 0, total: 1 });
    const notes = parseNotes(await ask(notesPrompt(text, { context }), { maxTokens: 3000 }));
    onProgress({ phase: "done", done: 1, total: 1 });
    return notes;
  }
  const partials = [];
  for (let i = 0; i < chunks.length; i++) {
    if (isStopped()) throw new Stopped();
    onProgress({ phase: "summarizing", done: i, total: chunks.length + 1 });
    partials.push(parseNotes(await ask(notesPrompt(chunks[i], { context, part: { index: i + 1, total: chunks.length } }), { maxTokens: 2500 })));
  }
  if (isStopped()) throw new Stopped();
  onProgress({ phase: "merging", done: chunks.length, total: chunks.length + 1 });
  const merged = parseNotes(await ask(mergePrompt(partials, { context }), { maxTokens: 3500 }));
  onProgress({ phase: "done", done: chunks.length + 1, total: chunks.length + 1 });
  return merged;
}

// segments: [{ blob, offsetSec }] ; stt(blob) → text
export async function transcribeSegments({ segments, stt, onProgress = () => {}, isStopped = () => false }) {
  const parts = [];
  for (let i = 0; i < segments.length; i++) {
    if (isStopped()) throw new Stopped();
    onProgress({ phase: "transcribing", done: i, total: segments.length });
    const t = String(await stt(segments[i].blob) || "").trim();
    if (t) parts.push(`[${fmtTime(segments[i].offsetSec)}] ${t}`);
  }
  onProgress({ phase: "transcribed", done: segments.length, total: segments.length });
  return parts.join("\n");
}

export function mailtoLink(email, to = "") {
  const q = new URLSearchParams({ subject: email.subject || "", body: email.body || "" }).toString().replace(/\+/g, "%20");
  return `mailto:${encodeURIComponent(to)}?${q}`;
}
